import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaFileAlt, FaImage, FaPlusCircle, FaTimes } from 'react-icons/fa';

const RessourceForm = () => {
    const [title, setTitle] = useState('');
    const [file, setFile] = useState(null);
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null); 
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Gestion de la sélection d'image avec prévisualisation
    const handleImageChange = (e) => {
        const selectedImage = e.target.files[0];
        if (selectedImage) {
            setImage(selectedImage);
            setImagePreview(URL.createObjectURL(selectedImage));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !file) {
            setError('Veuillez fournir un titre et sélectionner un fichier.');
            return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('file', file);
        if (image) {
            formData.append('image', image);
        }

        try {
            setLoading(true);
            setError('');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            await axios.post(`${API_URL}/api/ressources/ajouter`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLoading(false);
            navigate('/NosRessources');
        } catch (error) {
            setLoading(false);
            const errorMessage = error.response?.data?.message || 'Erreur lors de l\'ajout. Veuillez réessayer.';
            setError(errorMessage);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4">
            <div className="max-w-2xl mx-auto bg-white shadow-2xl rounded-[2rem] overflow-hidden">
                {/* Header du formulaire */}
                <div className="bg-[#1B365D] p-8 text-center text-white">
                    <FaCloudUploadAlt className="mx-auto text-5xl mb-4 text-yellow-400" />
                    <h1 className="text-3xl font-black uppercase tracking-tighter">Ajouter une Ressource</h1>
                    <p className="opacity-70 text-sm mt-2 font-bold tracking-widest uppercase">Bibliothèque HJS</p>
                </div>

                <form onSubmit={handleSubmit} className="p-10">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-red-700 font-medium flex justify-between items-center">
                            <span>{error}</span>
                            <FaTimes className="cursor-pointer" onClick={() => setError('')} />
                        </div>
                    )}

                    {/* Champ Titre */}
                    <div className="mb-8">
                        <label className="block text-[#1B365D] font-black uppercase text-xs tracking-widest mb-3">Titre de la ressource</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ex: Charte du bénéficiaire"
                            className="w-full px-5 py-4 border-2 border-gray-100 rounded-2xl focus:border-blue-500 outline-none transition font-medium"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        {/* Champ Fichier Principal */}
                        <div>
                            <label className="block text-[#1B365D] font-black uppercase text-xs tracking-widest mb-3">Fichier (PDF, Audio...)</label>
                            <label className={`group cursor-pointer flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-2xl transition-all ${file ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200 hover:border-blue-400'}`}>
                                <FaFileAlt className={`text-3xl mb-2 ${file ? 'text-green-500' : 'text-gray-400 group-hover:text-blue-500'}`} />
                                <span className="text-[10px] font-bold text-center uppercase tracking-tighter overflow-hidden w-full px-2">
                                    {file ? file.name : "Sélectionner le fichier"}
                                </span>
                                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} required />
                            </label>
                        </div>

                        {/* Champ Image d'illustration */}
                        <div>
                            <label className="block text-[#1B365D] font-black uppercase text-xs tracking-widest mb-3">Image d'illustration</label>
                            <label className="group cursor-pointer relative flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 hover:border-yellow-400 transition-all overflow-hidden h-full min-h-[120px]">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                                ) : (
                                    <>
                                        <FaImage className="text-3xl mb-2 text-gray-400 group-hover:text-yellow-500" />
                                        <span className="text-[10px] font-bold uppercase tracking-tighter">Ajouter une image</span>
                                    </>
                                )}
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                            </label>
                        </div>
                    </div>

                    {/* Bouton de soumission */}
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex items-center justify-center gap-3 py-5 rounded-2xl font-black uppercase tracking-widest text-sm transition shadow-xl ${loading ? 'bg-gray-400' : 'bg-[#1B365D] text-white hover:bg-blue-900 transform hover:-translate-y-1 hover:shadow-blue-200'}`}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <>
                                <FaPlusCircle className="text-xl" /> Ajouter à la bibliothèque
                            </>
                        )}
                    </button>

                    <button 
                        type="button"
                        onClick={() => navigate('/NosRessources')}
                        className="w-full mt-4 text-gray-400 text-[10px] font-bold uppercase tracking-widest hover:text-red-500 transition"
                    >
                        Annuler et retourner
                    </button>
                </form>
            </div>
        </div>
    );
};

export default RessourceForm;